import { FullName, IraAccountsTableUser } from 'services/requests';
import { convertDate, formatUSD, mapCustodianTypeKeyToValue, userStatus } from 'utils';
import { USER_TYPE } from 'constants/userTypes';
import TableSorting from 'components/Table/TableElements/TableSorting';
import TableActionMenu from 'components/Table/TableElements/TableActionMenu';
import { CustomColumn } from 'components/Table/Table.types';
import TableFilter from 'components/Table/TableElements/TableFilter';
import { isIraUser, mapIndependentIraTypeKeyToValue } from '../IraIndependentIra.utils';
import { DropdownFilters } from './IndependentIraTable.filters';

import styles from './IndependentIraTable.module.css';

export type MapData = IndependentIraUsersTableSign[];
export type IndependentIraUsersTableSign = Omit<IraAccountsTableUser, 'balance'> &
  FullName & {
    icon: JSX.Element;
    balance: string;
  };

export const mapData = (
  data: IraAccountsTableUser[] | undefined,
  onEdit: (user: IraAccountsTableUser) => void,
  prefetch: (id: string) => void,
  iraUserPrefetch: (id: string) => void,
): MapData | undefined =>
  data?.map((user: IraAccountsTableUser) => {
    const isIRA = isIraUser(user.userProfileType);
    const requiredPrefetch = isIRA ? iraUserPrefetch : prefetch;

    return {
      ...user,
      fullName: `${user.firstName} ${user.middleName ? user.middleName : ''} ${
        user.lastName
      }`,
      profileStatus: userStatus(user.profileStatus),
      userProfileType: mapIndependentIraTypeKeyToValue(user.userProfileType),
      custodianType: mapCustodianTypeKeyToValue(user.custodianType),
      balance: formatUSD(user.balance),
      created: convertDate(user.created),
      lastLogin: convertDate(user.lastLogin),
      icon: (
        <TableActionMenu
          user={user}
          onEdit={onEdit}
          onPrefetch={requiredPrefetch}
          userType={isIRA ? USER_TYPE.iraUser : USER_TYPE.independentIra}
        />
      ),
    };
  });

export const columns: CustomColumn<IndependentIraUsersTableSign>[] = [
  {
    Header: <TableSorting sortingFieldKey={'fullName'} sortingFieldName={'Name'} />,
    accessor: 'fullName',
    className: styles.nameColumn,
  },
  {
    Header: 'Username',
    accessor: 'userName',
    className: styles.usernameColumn,
  },
  {
    Header: <TableSorting sortingFieldKey={'email'} sortingFieldName={'Email'} />,
    accessor: 'email',
    className: styles.emailColumn,
  },
  {
    Header: <TableFilter {...DropdownFilters.profileStatus} />,
    accessor: 'profileStatus',
    className: styles.statusColumn,
  },
  {
    Header: <TableFilter {...DropdownFilters.userProfileType} />,
    accessor: 'userProfileType',
    className: styles.accountTypeColumn,
  },
  {
    Header: (
      <TableSorting
        sortingFieldKey={'accountNumber'}
        sortingFieldName={'Account number'}
      />
    ),
    accessor: 'accountNumber',
    className: styles.accountNumberColumn,
  },
  {
    Header: <TableFilter {...DropdownFilters.custodianType} />,
    accessor: 'custodianType',
    className: styles.custodianTypeColumn,
  },
  {
    Header: <TableSorting sortingFieldKey={'balance'} sortingFieldName={'Balance'} />,
    accessor: 'balance',
    className: styles.balanceColumn,
  },
  {
    Header: <TableSorting sortingFieldKey={'created'} sortingFieldName={'Created'} />,
    accessor: 'created',
    className: styles.createdColumn,
  },
  {
    Header: (
      <TableSorting sortingFieldKey={'lastLogin'} sortingFieldName={'Last login'} />
    ),
    accessor: 'lastLogin',
    className: styles.lastLoginColumn,
  },
  {
    Header: '',
    accessor: 'icon',
    className: styles.iconColumn,
  },
];
